import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { konstPromise } from '@rad/utility/promise';
import { useFetch, usePromise } from '@rad/hooks';
import createTable from '@rad/components/ui/table';
import Input from '@rad/components/inputs/input';
import Dropdown from '@rad/components/inputs/dropdown';
import { formatScorecardStatus, getLookups } from '../../types';
// const FilterDropdown = (props:Props) => {
// }
export const useScorecardTableFilters = () => React.useState(() => ((_) => true));
const SCORECARD_FILTERS_SESSION_KEY = 'admin.all-scorecards.filters';
const defaultScorecardFilters = () => ({
    'title': '',
    'active': true,
    'program': null,
    'timeframe': null,
    'owner': null,
    'status': null,
});
const parseScorecardFilters = (value) => {
    if (value === null)
        return null;
    try {
        const parsed = JSON.parse(value);
        const defaults = defaultScorecardFilters();
        return Object.fromEntries(Object.keys(defaults).map((key) => [
            key,
            Object.prototype.hasOwnProperty.call(parsed, key)
                ? parsed[key]
                : defaults[key],
        ]));
    }
    catch {
        return null;
    }
};
export const ScorecardTableFilters = (props) => {
    const scorecards = usePromise(props.scorecards) || [];
    const [lookupsPromise] = useFetch(getLookups());
    const lookups = usePromise(lookupsPromise) || [];
    const lookupTitle = (kind, value) => lookups.find((x) => x.kind === kind && x.value === value)?.title || value;
    const [stateFilters, setFilters] = React.useState(defaultScorecardFilters);
    const [stateHasSessionValue, setHasSessionValue] = React.useState(false);
    const [stateSessionLoaded, setSessionLoaded] = React.useState(false);
    const initialFilters = React.useRef(JSON.stringify(defaultScorecardFilters()));
    React.useEffect(() => {
        const stored = sessionStorage.getItem(SCORECARD_FILTERS_SESSION_KEY);
        const parsed = parseScorecardFilters(stored);
        if (parsed) {
            initialFilters.current = JSON.stringify(parsed);
            setFilters(parsed);
            setHasSessionValue(true);
        }
        setSessionLoaded(true);
    }, []);
    React.useEffect(() => {
        if (!stateSessionLoaded)
            return;
        const serialized = JSON.stringify(stateFilters);
        if (serialized === initialFilters.current)
            return;
        sessionStorage.setItem(SCORECARD_FILTERS_SESSION_KEY, serialized);
        setHasSessionValue(true);
    }, [stateFilters, stateSessionLoaded]);
    React.useEffect(() => {
        props.onFilterChange((x) => ((stateFilters.title === '' || x.display.toLowerCase().includes(stateFilters.title.toLowerCase()))
            && (stateFilters.program === null || x.program === stateFilters.program)
            && (stateFilters.timeframe === null || x.timeframe === stateFilters.timeframe)
            && (stateFilters.owner === null || x.owner === stateFilters.owner)
            && (stateFilters.status === null || x.status === stateFilters.status)
            && (stateFilters.active === null || x.is_active === stateFilters.active)));
    }, [stateFilters]);
    const setFilter = (w) => (x) => setFilters((s) => ({ ...s, [w]: x }));
    return _jsxs("div", { className: "flex-row", children: [_jsx(Input, { kind: "text", size: "sm", label: "Title", className: "me-1", value: stateFilters.title, onChange: (x) => setFilter('title')(x) }), _jsx(Dropdown, { size: "sm", label: "Program", className: "me-1", options: konstPromise([null, ...Array.from(new Set(scorecards.map((x) => x.program))).toSorted()]), value: stateFilters.program, optionKey: (x) => x, optionLabel: (x) => x === null ? 'Select...' : x, onChange: setFilter('program') }), _jsx(Dropdown, { size: "sm", label: "Active", className: "me-1", options: konstPromise([[null, 'Select...'], [true, 'Yes'], [false, 'No']]), value: stateFilters.active === null ? null : stateFilters.active ? 1 : 0, optionKey: (x) => x[0] === null ? null : x[0] ? 1 : 0, optionLabel: (x) => x[1], onChange: (x) => setFilter('active')(x === null ? null : x === 1 ? true : false) }), _jsx(Dropdown, { size: "sm", label: "Owner", className: "me-1", options: konstPromise([null, ...Array.from(new Set(scorecards.map((x) => x.owner))).toSorted()]), value: stateFilters.owner, optionKey: (x) => x, optionLabel: (x) => x === null ? 'Select...' : x, onChange: setFilter('owner') }), _jsx(Dropdown, { size: "sm", label: "Timeframe", className: "me-1", options: konstPromise([
                    null,
                    ...Array.from(new Set(scorecards.filter((x) => stateFilters.program === null || x.program === stateFilters.program).map((x) => x.timeframe))).toSorted()
                ]), value: stateFilters.timeframe, optionKey: (x) => x, optionLabel: (x) => x === null ? 'Select...' : lookupTitle('timeframe', x), onChange: setFilter('timeframe') }), _jsx(Dropdown, { size: "sm", label: "Status", className: "me-1", options: konstPromise([
                    null,
                    ...Array.from(new Set(scorecards.map((x) => x.status))).toSorted()
                ]), value: stateFilters.status, optionKey: (x) => x, optionLabel: (x) => x === null ? 'Select...' : x, onChange: setFilter('status') }), stateHasSessionValue && _jsx("button", { type: "button", className: "btn btn-sm btn-outline-secondary", onClick: () => {
                    const defaults = defaultScorecardFilters();
                    initialFilters.current = JSON.stringify(defaults);
                    sessionStorage.removeItem(SCORECARD_FILTERS_SESSION_KEY);
                    setFilters(defaults);
                    setHasSessionValue(false);
                }, children: "Clear" })] });
};
export const ScorecardTable = (props) => {
    const DataTable = createTable(props.scorecards);
    const [lookupsPromise] = useFetch(getLookups());
    const lookups = usePromise(lookupsPromise) || [];
    const lookupTitle = (kind, value) => lookups.find((x) => x.kind === kind && x.value === value)?.title || value;
    return _jsx(_Fragment, { children: _jsx(DataTable, { title: props.title, columns: [
                { 'header': 'Scorecard', 'render': (x) => _jsx("a", { href: `#/scorecards/${x.id}`, children: x.display }) },
                { 'header': 'Program', 'render': (x) => x.program },
                { 'header': 'Timeframe', 'render': (x) => lookupTitle('timeframe', x.timeframe) },
                { 'header': 'Owner', 'render': (x) => x.owner },
                { 'header': 'Status', 'render': (x) => formatScorecardStatus(x.status) },
                ...((f) => f ? [{ 'header': '', 'render': (x) => f(x), 'className': 'w-1' }] : [])(props.actions),
            ] }) });
};
